import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { format as formatDate, parseISO } from 'date-fns';
import { InlineSelect, InlineSelectDatePicker, MenuItem, Row, TextField, Tooltip, Typography } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import { ExclamationMarkFilled } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { BillingStageScheduleType, OneTimeInStagesModel, StageScheduleContext } from '~/models';
import { scheduleErrorMessage } from './scheduleErrorMessage';
import { defaultScheduleForType } from './scheduleForType';
import { getScheduleLabel } from './scheduleLabel';
import { getScheduleOptions, scheduleTextsPath, TODAY_OPTION_VALUE } from './scheduleOptions';
export const GetStageScheduleControlTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const StageScheduleControl = (param)=>{
    let { testId, schedule, context, effectiveDate = null, today = '', disabled, onChange } = param;
    const { t: texts } = useLocaleKeys(scheduleTextsPath);
    const { StageScheduleControlTestIds } = useMemo(()=>GetStageScheduleControlTID(testId), [
        testId
    ]);
    const options = useMemo(()=>getScheduleOptions({
            context,
            texts
        }), [
        context,
        texts
    ]);
    const error = OneTimeInStagesModel.isStageScheduleValid({
        schedule,
        context,
        effectiveDate,
        today
    });
    const errorText = error ? scheduleErrorMessage(error, texts) : '';
    const renderValue = ()=>{
        var _ref;
        var _options_find;
        const label = getScheduleLabel({
            schedule,
            context,
            texts,
            today
        });
        if (label) return label.label;
        return (_ref = (_options_find = options.find((option)=>option.value === schedule.type)) === null || _options_find === void 0 ? void 0 : _options_find.label) !== null && _ref !== void 0 ? _ref : '';
    };
    const isRelative = schedule.type === BillingStageScheduleType.RelativeToEffectiveDate;
    const days = isRelative ? schedule.relativeToEffectiveDate.days : 0;
    const [rawDays, setRawDays] = useState(!isRelative || days === 0 ? '' : String(days));
    useEffect(()=>{
        setRawDays(isRelative && days !== 0 ? String(days) : '');
    }, [
        schedule.type
    ]);
    const daysErrorText = isRelative && rawDays === '' ? texts.errors.daysRequired() : errorText;
    const isOnDate = schedule.type === BillingStageScheduleType.OnDate;
    const isOnDateToday = context === StageScheduleContext.Amendment && schedule.type === BillingStageScheduleType.OnDate && !!today && schedule.onDate.date === today;
    const dropdownValue = isOnDateToday ? TODAY_OPTION_VALUE : schedule.type;
    const onDateValue = isOnDate && schedule.type === BillingStageScheduleType.OnDate && schedule.onDate.date !== '' ? parseISO(schedule.onDate.date) : null;
    const withErrorTooltip = (child, tooltipText)=>error ? /*#__PURE__*/ _jsx(Tooltip, {
            title: tooltipText,
            testId: StageScheduleControlTestIds.ErrorTooltip(),
            disableFocusListener: true,
            disableTouchListener: true,
            children: /*#__PURE__*/ _jsx("span", {
                style: {
                    display: 'inline-flex'
                },
                children: child
            })
        }) : child;
    const dropdownHasError = !isRelative && !isOnDate && !!error;
    const pickerMinDate = useMemo(()=>{
        if (context === StageScheduleContext.ProposalOnEffectiveDate && effectiveDate) {
            return parseISO(effectiveDate);
        }
        return today ? parseISO(today) : undefined;
    }, [
        context,
        effectiveDate,
        today
    ]);
    const dropdown = /*#__PURE__*/ _jsx(InlineSelect, {
        bi: {
            name: 'stage_schedule'
        },
        skin: "primary",
        value: dropdownValue,
        testId: StageScheduleControlTestIds.Root(),
        disabled: disabled,
        error: dropdownHasError,
        helperText: dropdownHasError ? errorText : undefined,
        onChange: (event)=>{
            const newValue = event.target.value;
            if (newValue === dropdownValue) return;
            if (newValue === TODAY_OPTION_VALUE) {
                onChange({
                    type: BillingStageScheduleType.OnDate,
                    onDate: {
                        date: today
                    }
                });
                return;
            }
            onChange(defaultScheduleForType(newValue));
        },
        SelectProps: {
            renderValue,
            autoWidth: true
        },
        children: options.map((option)=>/*#__PURE__*/ _jsx(MenuItem, {
                value: option.value,
                testId: StageScheduleControlTestIds.Option(option.value),
                children: option.label
            }, option.value))
    });
    if (isRelative) {
        const daysInput = /*#__PURE__*/ _jsx(TextField, {
            bi: {
                name: 'stage_relative_days'
            },
            value: rawDays,
            onChange: (event)=>{
                const raw = event.target.value;
                setRawDays(raw);
                const parsed = parseInt(raw, 10);
                const nextDays = Number.isFinite(parsed) ? parsed : 0;
                onChange({
                    type: BillingStageScheduleType.RelativeToEffectiveDate,
                    relativeToEffectiveDate: {
                        days: nextDays
                    }
                });
            },
            testId: StageScheduleControlTestIds.Days(),
            disabled: disabled,
            width: 64,
            minWidth: 64,
            error: !!error,
            suffix: /*#__PURE__*/ _jsx("span", {
                style: {
                    display: 'inline-flex'
                },
                children: error && /*#__PURE__*/ _jsx(DaysErrorIcon, {
                    testId: StageScheduleControlTestIds.DaysErrorIcon()
                })
            }),
            inputProps: {
                type: 'number',
                min: 0,
                style: {
                    textAlign: 'center',
                    padding: '4px 2px',
                    height: 32,
                    boxSizing: 'border-box'
                }
            }
        });
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 4,
            alignItems: "center",
            wrap: false,
            children: [
                withErrorTooltip(daysInput, daysErrorText),
                /*#__PURE__*/ _jsx(Typography, {
                    children: texts.inline.days({
                        count: days
                    })
                }),
                dropdown
            ]
        });
    }
    if (isOnDate) {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 4,
            alignItems: "center",
            wrap: false,
            children: [
                dropdown,
                /*#__PURE__*/ _jsx(InlineSelectDatePicker, {
                    bi: {
                        name: 'stage_on_date'
                    },
                    skin: "primary",
                    value: onDateValue,
                    minDate: pickerMinDate,
                    emptyLabel: texts.placeholder.selectDate(),
                    testId: StageScheduleControlTestIds.Date(),
                    disabled: disabled,
                    error: !!error,
                    helperText: error ? errorText : undefined,
                    clearable: true,
                    clearLabel: texts.ctaLabels.clearDate(),
                    onChange: (date)=>{
                        onChange({
                            type: BillingStageScheduleType.OnDate,
                            onDate: {
                                date: date ? formatDate(date, 'yyyy-MM-dd') : ''
                            }
                        });
                    }
                })
            ]
        });
    }
    return dropdown;
};
const DaysErrorIcon = (param)=>{
    let { testId } = param;
    return /*#__PURE__*/ _jsx("span", {
        style: {
            color: colors.red970,
            display: 'inline-flex'
        },
        children: /*#__PURE__*/ _jsx(ExclamationMarkFilled, {
            size: 16,
            testId: testId
        })
    });
};
